import { FocusEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NavLink, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { usersApi } from "../../api";
import {
  APP_ROUTES,
  ERROR_MESSAGES,
  IAuthResponse,
  IResponseError,
  VALID_EMAIL_REGEX,
  VALIDATION_MESSAGES,
} from "../../data-access";
import { FormInput, FormButton, BrandLogo } from "../../ui";
import {
  notify,
  handleFailedRequest,
  sanitizeEmail,
  cookie,
  encryption,
  updateExtensionCookies,
} from "../../util";
import * as yup from "yup";
import "./login.scss";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from "@react-oauth/google";
import { storeBillingActions } from "../../store/slices/billing";
import { useDispatch } from "react-redux";
import { storeAuthActions } from "../../store/slices/auth";
import { syncAuthWithExtension } from "../../util/auth-sync";
import { AuthTestimonials } from "../auth";

interface ILoginFormData {
  email: string;
}

const validationScheme = yup
  .object({
    email: yup
      .string()
      .required("Please enter your email address")
      .matches(VALID_EMAIL_REGEX, VALIDATION_MESSAGES.INVALID_EMAIL),
  })
  .required();

export const Login = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isProcessingRequest, setIsProcessingRequest] =
    useState<boolean>(false);

  const form = useForm<ILoginFormData>({
    mode: "onBlur",
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(validationScheme),
  });
  const {
    control,
    resetField,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields },
  } = form;

  const handleFormSubmit = (formFields: ILoginFormData): void => {
    setIsProcessingRequest(true);

    usersApi
      .authenticate(formFields.email)
      .then(() => {
        notify("success", "Please check your email to confirm the login");
        resetField("email");
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsProcessingRequest(false));
  };

  const handleGoogleFormSubmit = (formFields: CredentialResponse): void => {
    setIsProcessingRequest(true);

    usersApi
      .googleAuthenticate(formFields.credential)
      .then((response: IAuthResponse) => {
        const authDetails = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          user: response.user,
        };

        dispatch(storeAuthActions.setUserData(authDetails));
        syncAuthWithExtension("AUTH_LOGIN", {
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          user: response.user,
        });

        if (response.user.promocode) {
          dispatch(
            storeBillingActions.update({ discount: response.user.promocode })
          );
        }

        cookie.set("exSM", encryption.generateHash("authorized"));
        updateExtensionCookies(authDetails);
        navigate(APP_ROUTES.dashboard);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsProcessingRequest(false));
  };

  const handleEmailInput = (e: FocusEvent<HTMLInputElement>): void => {
    const sanitizedEmail = sanitizeEmail(e?.target?.value);
    setValue("email", sanitizedEmail, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const isFormValid =
    Object.keys(dirtyFields).length > 0 && !errors?.email?.message;

  return (
    <div className="login-page">
      <BrandLogo />
      <AuthTestimonials variant="login" />

      <div className="login-page__form-side">
        <div className="login-page__form-container">

          <div className="login-page__header">
            <h1 className="login-page__title">Welcome back</h1>
            <p className="login-page__subtitle">
              Sign in to continue to your dashboard
            </p>
          </div>

          <div className="login-page__google-wrapper">
            <div className="login-page__google-custom">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Continue with Google</span>
            </div>
            <div
              className={`login-page__google-btn${isProcessingRequest ? " login-page__google-btn--disabled" : ""}`}
            >
              <GoogleOAuthProvider
                clientId={`${process.env.REACT_APP_AUTH_CLIENT_ID}`}
              >
                <GoogleLogin
                  size="large"
                  width="400"
                  locale="en"
                  text="continue_with"
                  shape="rectangular"
                  onSuccess={(response: CredentialResponse) => {
                    handleGoogleFormSubmit(response);
                  }}
                  onError={() => {
                    notify("error", ERROR_MESSAGES.GENERIC);
                  }}
                />
              </GoogleOAuthProvider>
            </div>
          </div>

          <div className="login-page__divider">
            <span>or continue with email</span>
          </div>

          <form
            className="login-page__form"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <Controller
              control={control}
              name="email"
              render={({ field: { name, onBlur, value, ref } }) => (
                <FormInput
                  ref={ref}
                  placeholder="Enter your email address"
                  error={errors?.email?.message}
                  name={name}
                  value={value}
                  disabled={isProcessingRequest}
                  onBlur={onBlur}
                  onChange={handleEmailInput}
                />
              )}
            />
            <FormButton
              className="login-page__submit-btn"
              theme="green-light"
              htmlType="submit"
              loading={isProcessingRequest}
              disabled={!isFormValid || isProcessingRequest}
            >
              Send magic link
            </FormButton>
          </form>

          <div className="login-page__footer">
            Don't have an account?
            <NavLink to={APP_ROUTES.registration}>Sign up</NavLink>
          </div>
        </div>
      </div>
    </div>
  );
};
